import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { rpcTriagem } from '@/components/kanban/triagem/rpcTriagem';

/* O crédito na Claro (Douglas 22/10/2026): os dados da triagem do Crédito —
   códigos CR, estado de cada cotação na Claro, gerentes e o retorno da defesa. */

export interface CodigoCr { codigo: string; nome: string; defesa_leva: string | null }
export interface GerenteClaro { id: string; nome: string; cargo: string | null; email: string | null; telefone: string | null }
interface EmailClaro { numero: number; enviado_em: string; por: string | null; resposta_em: string | null; status: string; erro: string | null }
export interface EstadoClaro {
  pedido_id: string; cotacao: string | null; emails: EmailClaro[];
  pode_cobrar: boolean; bloqueio: string | null; ultimo_email_em: string | null;
  ultima_defesa: { numero: string; em: string; retorno: string | null; retorno_em: string | null } | null;
}
export interface CotacaoNaClaro { pedidoId: string; cotacao: string | null; numeroPedido: string; cliente: string; pipefy: string | null; codigos: string[] }

const chave = (pedidoId: string, cotacao: string | null) => `${pedidoId}:${cotacao || ''}`;

export async function chamarCreditoClaro<T>(path: string, metodo: 'GET' | 'POST' = 'GET', body?: unknown): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sua sessão expirou. Entre novamente.');
  const base = ((import.meta.env.VITE_SUPABASE_URL as string) || 'https://beta.crm-api.primetecia.com.br/api').replace(/\/$/, '');
  const r = await fetch(`${base}/v1/credito/claro/${path}`, {
    method: metodo,
    headers: { Authorization: `Bearer ${session.access_token}`, ...(metodo === 'POST' ? { 'Content-Type': 'application/json' } : {}) },
    body: metodo === 'POST' ? JSON.stringify(body ?? {}) : undefined,
  });
  const corpo = await r.json().catch(() => ({ error: 'O CRM não respondeu corretamente.' }));
  if (!r.ok) throw new Error(corpo.error || 'Não foi possível concluir.');
  return corpo.data as T;
}

export const ROTULO_RETORNO: Record<string, string> = { aprovou: 'aprovou', manteve: 'manteve a recusa', sem_resposta: 'sem resposta' };

export function useCodigosCr() {
  return useQuery({ queryKey: ['credito-codigos-cr'], staleTime: 30 * 60_000, queryFn: () => chamarCreditoClaro<CodigoCr[]>('codigos') });
}

export function useCreditoNaClaro(cotacoes: CotacaoNaClaro[], ativo: boolean) {
  const qc = useQueryClient();
  const lista = useMemo(() => cotacoes.map(c => ({ pedidoId: c.pedidoId, cotacao: c.cotacao })), [cotacoes]);
  const estado = useQuery({
    queryKey: ['credito-na-claro', lista],
    enabled: ativo && lista.length > 0,
    refetchInterval: 120_000,
    queryFn: () => chamarCreditoClaro<EstadoClaro[]>('estado', 'POST', { cotacoes: lista }),
  });
  const gerentes = useQuery({ queryKey: ['claro-gerentes'], enabled: ativo, staleTime: 10 * 60_000, queryFn: () => chamarCreditoClaro<GerenteClaro[]>('gerentes') });
  const porCotacao = useMemo(() => new Map((estado.data ?? []).map(e => [chave(e.pedido_id, e.cotacao), e])), [estado.data]);
  const recarregar = () => qc.invalidateQueries({ queryKey: ['credito-na-claro'] });
  const retorno = useMutation({
    mutationFn: async (b: { defesaIds: string[]; retorno: string; codigos: string[]; obs: string }) => {
      const { error } = await rpcTriagem('fn_credito_registrar_retorno', { p_defesa_ids: b.defesaIds, p_retorno: b.retorno, p_codigos: b.codigos, p_obs: b.obs });
      if (error) throw new Error(error.message || 'Não foi possível registrar o retorno.');
    },
    onSuccess: () => { recarregar(); },
  });
  return { estadoDe: (pedidoId: string, cotacao: string | null) => porCotacao.get(chave(pedidoId, cotacao)), gerentes: gerentes.data ?? [], retorno, recarregar };
}

